import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Calendar as ReactCalendar} from 'react-calendar';
import {format, isValid, parse} from 'date-fns';
import {useVacationPeriods, useSaveVacationPeriod, useDeleteVacationPeriod} from '../../../appolo/admin.queries';
import Button from '../../UI/Button/Button';
import buttonClasses from '../../UI/Button/Button.module.scss';
import classes from './Users.module.scss';

const emptyPeriod = {dateFrom: '', dateTill: ''};
const displayDate = value => value.split('-').reverse().join('.');

const readCalendarDate = value => {
    if (!/^\d{2}\.\d{2}\.\d{4}$/.test(value)) return null;
    const date = parse(value, 'dd.MM.yyyy', new Date(2000, 0, 1));
    return isValid(date) ? date : null;
};

function VacationDateField({id, label, value, onChange, disabled}) {
    const {t, i18n} = useTranslation();
    const [open, setOpen] = useState(false);
    const locale = {ru: 'ru-RU', lv: 'lv-LV', en: 'en-GB'}[i18n.language] || 'en-GB';

    return <div className={classes.vacationDateField}
        onBlur={event => {if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);}}
        onKeyDown={event => {if (event.key === 'Escape') setOpen(false);}}>
        <label htmlFor={id}>{label}</label>
        <div className={classes.vacationDateEntry}>
            <input id={id} type="text" inputMode="numeric" placeholder="dd.mm.yyyy" maxLength={10}
                required autoComplete="off" value={value} disabled={disabled}
                onChange={event => onChange(event.target.value)} />
            <button type="button" disabled={disabled} aria-label={`${t('Choose date')}: ${label}`}
                aria-expanded={open} aria-controls={`${id}-calendar`} onClick={() => setOpen(!open)}>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" />
                </svg>
            </button>
        </div>
        {open && !disabled && <div id={`${id}-calendar`} className={classes.vacationPicker}>
            <ReactCalendar locale={locale} value={readCalendarDate(value)} prev2Label={null} next2Label={null}
                onChange={date => {onChange(format(date, 'dd.MM.yyyy')); setOpen(false);}} />
        </div>}
    </div>;
}

export default function VacationPeriods() {
    const {t} = useTranslation();
    const {data, loading, error, refetch} = useVacationPeriods();
    const [savePeriod, {loading: saving}] = useSaveVacationPeriod();
    const [deletePeriod, {loading: deleting}] = useDeleteVacationPeriod();
    const [editing, setEditing] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [message, setMessage] = useState('');
    const [failure, setFailure] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    const busy = saving || deleting || refreshing;

    const errorMessage = (exception, fallback) => {
        const errors = exception.graphQLErrors?.length ? exception.graphQLErrors : exception.networkError?.result?.errors;
        const known = ['Vacation start must not be after its end.', 'Vacation periods must not overlap.',
            'Vacation period was not found.', 'Forbidden'];
        const detail = errors?.find(item => known.includes(item.message));
        return detail ? detail.message : fallback;
    };

    const startEditing = period => {
        setEditing(period ? {id: period.id, dateFrom: displayDate(period.dateFrom), dateTill: displayDate(period.dateTill)} : {...emptyPeriod});
        setPendingDelete(null);
        setFailure('');
        setMessage('');
    };

    const refresh = async () => {
        setRefreshing(true);
        try { await refetch(); }
        catch { setFailure('Saved, but the list could not be refreshed. Reload the page.'); }
        finally { setRefreshing(false); }
    };

    const onSave = async event => {
        event.preventDefault();
        if (busy) return;
        setFailure('');
        setMessage('');
        if (!editing.dateFrom || !editing.dateTill) {
            setFailure('Select both vacation dates.');
            return;
        }
        const dateFrom = readCalendarDate(editing.dateFrom);
        const dateTill = readCalendarDate(editing.dateTill);
        if (!dateFrom || !dateTill) {
            setFailure('Enter dates in dd.mm.yyyy format.');
            return;
        }
        if (dateFrom > dateTill) {
            setFailure('Vacation start must not be after its end.');
            return;
        }
        try {
            await savePeriod({variables: {input: {...editing,
                dateFrom: format(dateFrom, 'yyyy-MM-dd'), dateTill: format(dateTill, 'yyyy-MM-dd')}}});
            setEditing(null);
            setMessage('Vacation period saved.');
            await refresh();
        } catch (exception) {
            setFailure(errorMessage(exception, 'Could not save the vacation period.'));
        }
    };

    const onDelete = async () => {
        if (busy || !pendingDelete) return;
        setFailure('');
        setMessage('');
        try {
            await deletePeriod({variables: {id: pendingDelete.id}});
            setPendingDelete(null);
            setMessage('Vacation period deleted.');
            await refresh();
        } catch (exception) {
            setFailure(errorMessage(exception, 'Could not delete the vacation period.'));
        }
    };

    return (
        <section className={classes.vacations} aria-labelledby="vacations-heading">
            <h2 id="vacations-heading">{t('Vacations')}</h2>
            <p>{t('Vacation dates are unavailable for orders in all offices. Both dates are included.')}</p>
            {message && <p role="status" className={classes.notice}>{t(message)}</p>}
            {loading && <p role="status">{t('Loading vacation periods...')}</p>}
            {error && <div role="alert">
                <p>{t('Could not load vacation periods.')}</p>
                <Button type="secondary" onClick={() => refetch().catch(() => {})}>{t('Retry')}</Button>
            </div>}
            {!loading && !error && <>
                {data?.vacationPeriods?.length ? <div className={classes.vacationTableWrapper}>
                    <table className={classes.vacationTable}>
                        <thead><tr><th scope="col">{t('Start date')}</th><th scope="col">{t('End date (inclusive)')}</th><th scope="col">{t('Actions')}</th></tr></thead>
                        <tbody>{data.vacationPeriods.map(period => <tr key={period.id}>
                            <td>{displayDate(period.dateFrom)}</td><td>{displayDate(period.dateTill)}</td>
                            <td className={classes.vacationActions}>
                                <Button type="secondary" disabled={busy} onClick={() => startEditing(period)}>{t('Edit')}</Button>
                                <Button type="secondary" disabled={busy} onClick={() => {
                                    setPendingDelete(period); setEditing(null); setFailure(''); setMessage('');
                                }}>{t('Delete')}</Button>
                            </td>
                        </tr>)}</tbody>
                    </table>
                </div> : <p>{t('No vacation periods. All dates follow the normal scheduling rules.')}</p>}
                {!editing && !pendingDelete && <Button type="primary" disabled={busy} onClick={() => startEditing(null)}>{t('Add vacation period')}</Button>}
            </>}
            {failure && <p id="vacation-error" role="alert" className={classes.failure}>{t(failure)}</p>}
            {pendingDelete && <div className={classes.vacationForm}>
                <p>{t('Delete vacation period?')} {displayDate(pendingDelete.dateFrom)} – {displayDate(pendingDelete.dateTill)}</p>
                <Button type="primary" disabled={busy} onClick={onDelete}>{t('Delete')}</Button>
                <Button type="secondary" disabled={busy} onClick={() => setPendingDelete(null)}>{t('Cancel')}</Button>
            </div>}
            {editing && <form className={classes.vacationForm} onSubmit={onSave} noValidate aria-describedby={failure ? 'vacation-error' : undefined}>
                <h3>{t(editing.id ? 'Edit vacation period' : 'Add vacation period')}</h3>
                <div className={classes.vacationFields}>
                    <VacationDateField id="vacation-from" label={t('Start date')} value={editing.dateFrom} disabled={busy}
                        onChange={value => setEditing({...editing, dateFrom: value})} />
                    <VacationDateField id="vacation-till" label={t('End date (inclusive)')} value={editing.dateTill} disabled={busy}
                        onChange={value => setEditing({...editing, dateTill: value})} />
                </div>
                <button type="submit" disabled={busy} className={`${buttonClasses.Button} ${buttonClasses.primary}`}>{t(busy ? 'Saving...' : 'Save')}</button>
                <button type="button" disabled={busy} className={buttonClasses.Button} onClick={() => {setEditing(null); setFailure('');}}>{t('Cancel')}</button>
            </form>}
        </section>
    );
}
